import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Search, 
  Filter, 
  Clock,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  X
} from 'lucide-react';
import { cn, getStatusColor, getRiskBandColor, getPriorityColor, formatRelativeTime } from '../lib/utils';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'more_info', label: 'More Info' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'fraud_flagged', label: 'Fraud Flagged' },
];

const COUNTRY_OPTIONS = [
  { value: 'all', label: 'All Countries' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'KE', label: 'Kenya' },
  { value: 'GH', label: 'Ghana' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'TZ', label: 'Tanzania' },
];

const RISK_OPTIONS = [
  { value: 'all', label: 'All Risk Levels' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

export function CaseQueue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    country: searchParams.get('country') || 'all',
    risk_band: searchParams.get('risk_band') || 'all',
    overdue_only: searchParams.get('overdue_only') === 'true'
  });

  const fetchCases = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.country && filters.country !== 'all') {
        params.append('country', filters.country);
      }
      if (filters.risk_band && filters.risk_band !== 'all') {
        params.append('risk_band', filters.risk_band);
      }
      if (filters.overdue_only) {
        params.append('overdue_only', 'true');
      }
      
      const response = await axios.get(`${API_URL}/api/cases?${params.toString()}`);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'all' && v !== false) {
        params.set(k, v.toString());
      }
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      country: 'all',
      risk_band: 'all',
      overdue_only: false
    });
    setSearchParams({});
  };

  const hasActiveFilters = filters.status !== 'all' || 
    filters.country !== 'all' || 
    filters.risk_band !== 'all' || 
    filters.overdue_only;

  const filteredCases = searchQuery 
    ? cases.filter(c => 
        c.applicant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.applicant_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.id.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : cases;

  return (
    <div className="p-6 space-y-6" data-testid="case-queue">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Case Queue</h1>
          <p className="text-muted-foreground mt-1">
            {filteredCases.length} cases {hasActiveFilters && '(filtered)'}
          </p>
        </div>
        <div className="flex gap-2">
          <PageHelp pageKey="caseQueue" />
          <Button variant="outline" size="sm" onClick={fetchCases}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by name, email, ID..." 
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            
            <Select 
              value={filters.status} 
              onValueChange={(v) => handleFilterChange('status', v)}
            >
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.country} 
              onValueChange={(v) => handleFilterChange('country', v)}
            >
              <SelectTrigger className="w-[150px]" data-testid="country-filter">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.risk_band} 
              onValueChange={(v) => handleFilterChange('risk_band', v)}
            >
              <SelectTrigger className="w-[150px]" data-testid="risk-filter">
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                {RISK_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button 
              variant={filters.overdue_only ? "default" : "outline"} 
              size="sm"
              onClick={() => handleFilterChange('overdue_only', !filters.overdue_only)}
              data-testid="overdue-filter"
            >
              <Clock className="h-4 w-4 mr-1" />
              Overdue
            </Button>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cases Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          ) : filteredCases.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCases.map((caseItem) => (
                  <TableRow 
                    key={caseItem.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid={`case-row-${caseItem.id}`}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{caseItem.applicant_name}</p>
                        <p className="text-sm text-muted-foreground font-mono">
                          {caseItem.id.slice(0, 8)}...
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{caseItem.country}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn(getStatusColor(caseItem.status))}>
                          {caseItem.status.replace('_', ' ')}
                        </Badge>
                        {caseItem.is_overdue && (
                          <Badge variant="destructive" className="text-xs px-1.5">
                            <Clock className="h-3 w-3" />
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn(getRiskBandColor(caseItem.risk_band))}>
                        {caseItem.risk_band}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn(getPriorityColor(caseItem.priority))}>
                        {caseItem.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatRelativeTime(caseItem.created_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/cases/${caseItem.id}`}>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Filter className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No cases found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CaseQueue;
